import type { FC } from 'react';

import styled, { keyframes } from 'styled-components';

// Self-contained loading screen used as the top-level Suspense fallback in
// index.tsx. It renders ABOVE the `<rd-theme-provider>` element (which lives
// inside App), so the `--nova-*` theme tokens are NOT available here, and it
// must not read i18n (unlike Components/Loading, which calls useTranslation and
// would itself suspend at this top-level boundary). Colours are therefore
// hardcoded.
//
// The spinner replicates @radius/ui-library's Preloader (the `md`/primary
// variant) — the same 4-div ring, 1.2s cubic-bezier rotation and staggered
// animation delays. This shell is locked to the light theme
// (App.tsx data-theme="light"), so the values are the light-theme tokens:
//   - background: backgroundLevel2 (#ffffff)
//   - ring colour: tokens.ui03     (#000000)
// Keep these in sync with the theme tokens if the shell's theme changes.
const BACKGROUND_LEVEL2_LIGHT = '#ffffff';
const UI03_LIGHT = '#000000';

// md size from the Preloader SIZES map: container 3rem, border 3px.
const SPINNER_SIZE_REM = 3;
const SPINNER_BORDER_PX = 3;

const rotate = keyframes`
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
`;

const Screen = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	width: 100%;
	background-color: ${BACKGROUND_LEVEL2_LIGHT};
`;

const Spinner = styled.div`
	display: inline-block;
	position: relative;
	width: ${SPINNER_SIZE_REM}rem;
	height: ${SPINNER_SIZE_REM}rem;

	div {
		display: block;
		position: absolute;
		box-sizing: border-box;
		width: ${SPINNER_SIZE_REM}rem;
		height: ${SPINNER_SIZE_REM}rem;
		border: ${SPINNER_BORDER_PX}px solid transparent;
		border-top-color: ${UI03_LIGHT};
		border-radius: 50%;
		animation: ${rotate} 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;

		@media (prefers-reduced-motion: reduce) {
			animation-duration: 3s;
		}
	}

	> div:nth-child(1) {
		animation-delay: -0.45s;
	}

	> div:nth-child(2) {
		animation-delay: -0.3s;
	}

	> div:nth-child(3) {
		animation-delay: -0.15s;
	}
`;

const ShellLoadingScreen: FC = () => (
	<Screen role="status" aria-live="polite" aria-busy="true">
		<Spinner aria-hidden="true">
			<div />
			<div />
			<div />
			<div />
		</Spinner>
	</Screen>
);

export default ShellLoadingScreen;
